/* --------------------------------------------------------------------------
   Artikelsökning

   Ett sökord letar i benämning och artikelnummer: "kabel" hittar alla kablar,
   "100" alla artiklar vars nummer eller namn innehåller 100.

   Med kommatecken blir sökningen en lista med exakta artikelnummer:
   "100, 105, 210" hittar just de tre artiklarna och inte 1000, 2100 och
   alla andra som innehåller sifferkombinationen. "100," ger bara artikel 100.
   Ett led med bokstäver, "kabel, 100", söker dessutom i benämningen, så att
   en lista kan blanda nummer och namn. Semikolon fungerar som kommatecken,
   eftersom en lista inklistrad från Excel ofta har det.

   Ett decimalkomma är inget listtecken: "1,5" kan vara "Läsk 1,5 l". Därför
   letar en lista också efter hela söktexten i benämningen. Hittar den något
   där redovisas inga nummer som saknade, eftersom kunden då sökte på namn.
   -------------------------------------------------------------------------- */

const SEP = /[,;]/;
const LETTER = /\p{L}/u;

/**
 * Tolkar sökfältet. Ger null för tomt, annars
 *   {mode:'text', text}        ett sökord
 *   {mode:'list', tokens}      kommaseparerad lista, dubbletter borttagna
 */
export function parseProductQuery(q){
  const raw = String(q ?? '').trim();
  if(!raw) return null;
  if(!SEP.test(raw)) return {mode:'text', text: raw.toLowerCase()};
  const seen = new Set(), tokens = [];
  for(const t of raw.split(SEP).map(x => x.trim()).filter(Boolean)){
    if(!seen.has(t.toLowerCase())){ seen.add(t.toLowerCase()); tokens.push(t); }
  }
  return tokens.length ? {mode:'list', tokens, text: raw.toLowerCase()} : null;
}

/**
 * Filtrerar artiklar efter sökningen. items har {name, articleNumber}.
 * Ger {items, missing}: missing är listans led som inte gav någon träff,
 * så att kunden ser vilka nummer som inte såldes i urvalet.
 */
export function searchProducts(items, q){
  const query = typeof q === 'string' ? parseProductQuery(q) : q;
  if(!query) return {items, missing: [], query: null};

  if(query.mode === 'text'){
    const t = query.text;
    return {items: items.filter(p => p.name.toLowerCase().includes(t) ||
                                     p.articleNumber.toLowerCase().includes(t)),
            missing: [], query};
  }

  const hit = new Set();
  const matchers = query.tokens.map(tok => {
    const lower = tok.toLowerCase();
    const byName = LETTER.test(tok);
    return {tok, test: p => p.articleNumber.trim().toLowerCase() === lower ||
                            (byName && p.name.toLowerCase().includes(lower))};
  });
  let byText = false;
  const out = items.filter(p => {
    let any = false;
    for(const m of matchers) if(m.test(p)){ hit.add(m.tok); any = true; }
    if(p.name.toLowerCase().includes(query.text)){ byText = true; any = true; }
    return any;
  });
  return {items: out, missing: byText ? [] : query.tokens.filter(t => !hit.has(t)), query};
}

/** Sant när sökningen är ett ensamt nummer som också är ett helt artikelnummer. */
export function hasExactNumber(items, q){
  const query = parseProductQuery(q);
  if(!query || query.mode !== 'text' || LETTER.test(query.text)) return false;
  return items.some(p => p.articleNumber.trim().toLowerCase() === query.text);
}
